import { apiFetch } from '@/lib/api-client';
import type { ListeningEventPayload, PresignedUrl, StoryDetail, StoryListEnvelope } from './types';

export function fetchStoryById(storyId: string): Promise<StoryDetail> {
  return apiFetch<StoryDetail>(`/api/stories/${storyId}`);
}

/**
 * List the authenticated user's stories. Default order: newest-first
 * (backend `createdAt DESC`). Owner-scoped on the server — the JWT
 * user is the only identity the service filters by.
 *
 * Query params map to the backend `ListStoriesQueryDto`:
 *   - `worldId`   — single world (backwards-compat alias for worldIds[0])
 *   - `worldIds`  — multi-select world UUIDs (Batch 8)
 *   - `childIds`  — multi-select child UUIDs (Batch 8)
 *   - `year`      — calendar-year filter (Batch 8)
 *   - `month`     — calendar-month filter; requires `year` (Batch 8)
 *   - `search`    — ILIKE on title (Batch 8)
 *   - `isFavorite` — true/false strict match (undefined returns all)
 *   - `limit`      — page size, default 50 server-side
 *   - `offset`     — page offset, default 0 server-side
 *
 * Batch 8 envelope: returns `{ items, total }` — total reflects the
 * filtered count (NOT capped by limit), drives infinite-scroll
 * load-more gating + the "{count} Geschichten" subtitle.
 *
 * Response items carry `childName` + `previewText` (Batch 7 + Batch 8
 * backend extensions) so list rows can render "für {name}" + the
 * 200-char body preview without a second round-trip.
 */
export function fetchStoriesList(params?: {
  worldId?: string;
  worldIds?: string[];
  childIds?: string[];
  year?: number;
  month?: number;
  search?: string;
  isFavorite?: boolean;
  limit?: number;
  offset?: number;
}): Promise<StoryListEnvelope> {
  const qs = new URLSearchParams();
  if (params?.worldId) qs.set('worldId', params.worldId);
  if (params?.worldIds && params.worldIds.length > 0) {
    for (const id of params.worldIds) qs.append('worldIds', id);
  }
  if (params?.childIds && params.childIds.length > 0) {
    for (const id of params.childIds) qs.append('childIds', id);
  }
  if (params?.year !== undefined) qs.set('year', String(params.year));
  if (params?.month !== undefined) qs.set('month', String(params.month));
  if (params?.search && params.search.trim()) qs.set('search', params.search.trim());
  if (params?.isFavorite !== undefined) qs.set('isFavorite', String(params.isFavorite));
  if (params?.limit !== undefined) qs.set('limit', String(params.limit));
  if (params?.offset !== undefined) qs.set('offset', String(params.offset));
  const query = qs.toString();
  return apiFetch<StoryListEnvelope>(`/api/stories${query ? `?${query}` : ''}`);
}

export function patchStoryFavorite(storyId: string, favorite: boolean): Promise<StoryDetail> {
  return apiFetch<StoryDetail>(`/api/stories/${storyId}/favorite`, {
    method: 'PATCH',
    body: { favorite },
  });
}

/**
 * Batch 8 Commit 0 — DELETE /api/stories/:id. Backend cascade is
 * service-side (listening_events + story_engagements pre-deleted,
 * story-row deleted, FK Cascade cleans story_additional_children;
 * SET NULL applies to story_jobs / moderation_results /
 * series_episodes / book_orders). GCS asset cleanup is best-effort.
 *
 * Owner-scoped 404 on missing/cross-user (story_not_found envelope).
 *
 * Caller responsibilities:
 *   - clearStoryCountCache() so the AccountRoute Bibliothek-badge
 *     refreshes on next mount
 *   - update local items state (filter the deleted row out)
 *   - decrement local total counter for the subtitle
 */
export function deleteStory(storyId: string): Promise<{ message: string }> {
  return apiFetch<{ message: string }>(`/api/stories/${storyId}`, {
    method: 'DELETE',
  });
}

export interface SubmitRatingResponse {
  id: string;
  ratingStars: number;
  ratingText: string | null;
  /** ISO-8601 timestamp of the upsert. */
  ratedAt: string;
}

/**
 * Upsert a rating on a story. `POST /api/stories/:id/rating`, body
 * `{ rating: 1..5, comment?: string (≤2000 chars) }`.
 *
 * Payload-key note: Webflow's legacy shape was `{ rating, rating_comment }`.
 * The new backend DTO accepts `comment` (per Phase 2I Story-Ratings
 * service). Do NOT send `rating_comment` — it will be rejected by
 * `class-validator` at the DTO whitelist. Also do NOT include
 * `request_id` — the backend identifies the story via the URL param
 * and the user via the JWT.
 *
 * Re-rating overwrites (service-side upsert). Returns the persisted
 * fields for optimistic reconciliation on the client.
 */
export function submitRating(
  storyId: string,
  payload: { rating: number; comment?: string },
): Promise<SubmitRatingResponse> {
  return apiFetch<SubmitRatingResponse>(`/api/stories/${storyId}/rating`, {
    method: 'POST',
    body: payload,
  });
}

/**
 * Sign a 1-hour playback URL for the story's audio asset.
 * 404 `story_not_found` or 409 `story_audio_not_ready` via ApiError envelope.
 */
export function getPlaybackUrl(storyId: string): Promise<PresignedUrl> {
  return apiFetch<PresignedUrl>(`/api/stories/${storyId}/playback`);
}

/**
 * Sign a 1-hour PDF URL for the story's PDF asset (Batch 12 Sub-phase A).
 * Mirrors `getPlaybackUrl()` — backend `StoriesController.getPdfUrl()` returns
 * `{ url, expiresAt }` per the same StorageLocation pattern. Errors:
 *   - 404 `story_not_found` (cross-user access or missing id)
 *   - 409 `story_pdf_not_ready` (race: pdfStatus='ready' but signing in flight)
 */
export function getPdfUrl(storyId: string): Promise<PresignedUrl> {
  return apiFetch<PresignedUrl>(`/api/stories/${storyId}/pdf-url`);
}

/**
 * POST a playback event for BI analytics. Fire-and-forget — callers
 * `void` the promise; backend 4xx/5xx is logged but never surfaces to UI.
 */
export function recordListeningEvent(payload: ListeningEventPayload): Promise<{ id: string }> {
  return apiFetch<{ id: string }>('/api/listening-events', {
    method: 'POST',
    body: payload,
  });
}

/**
 * `pagehide` / `beforeunload` variant of recordListeningEvent. Uses
 * `keepalive: true` so the request continues after page tear-down
 * (Safari 13+, Chrome 66+, Firefox 83+). Payload is capped at 64 KB by
 * the spec — our event body is ~200 bytes. Silent on failure (BI fidelity
 * trade-off accepted for unload telemetry).
 *
 * sendBeacon is NOT used here because it cannot attach the Authorization
 * header required by this endpoint; `fetch` with `keepalive` is the
 * token-auth-compatible path.
 */
export function recordListeningEventBeacon(payload: ListeningEventPayload): void {
  void apiFetch<{ id: string }>('/api/listening-events', {
    method: 'POST',
    body: payload,
    keepalive: true,
  }).catch(() => {
    // Silent — we cannot await inside a pagehide handler anyway.
  });
}

/**
 * FIX A — POST /api/stories/:id/retrofit-audio.
 *
 * Adds audio to a text-only or previously-failed story. Costs NO credit.
 * `narratorGender` is required — the user never chose one for a text-only
 * story at creation time. Returns 202 with `{status:'enqueued', jobId}`;
 * a subsequent GET /api/stories/:id will show `audio_status: 'pending'`
 * once the DB flip lands (inside the same transaction as the enqueue).
 */
export function retrofitAudio(
  storyId: string,
  narratorGender: 'male' | 'female',
): Promise<{ status: 'enqueued'; jobId: string }> {
  return apiFetch<{ status: 'enqueued'; jobId: string }>(`/api/stories/${storyId}/retrofit-audio`, {
    method: 'POST',
    body: { narratorGender },
  });
}

/**
 * FIX A — POST /api/stories/:id/retrofit-pdf. No body. Same 202 shape.
 */
export function retrofitPdf(storyId: string): Promise<{ status: 'enqueued'; jobId: string }> {
  return apiFetch<{ status: 'enqueued'; jobId: string }>(`/api/stories/${storyId}/retrofit-pdf`, {
    method: 'POST',
  });
}
